import { Fragment, type ReactElement, type ReactNode, type Ref } from 'react';
import type { CatalogRole, PanelRole } from '@citibox-panel/api-client';
import { Box, Checkbox, Stack, Text, TextField } from '@citibox-panel/ui';
import { isKnownPermission, type Permission } from '@/lib/access';
import { isEmptyDraft, type AssignmentDraft } from '../lib/assignments';
import { permissionLabel } from '../lib/catalog';
import { hasChanges, type ChangeSummary } from '../lib/changes';
import { RoleChip } from './RoleChip';

// TODO: i18n
const COPY = {
  email: 'Email',
  emailPlaceholder: 'nombre@citibox.com',
  roles: 'Roles',
  extras: 'Permisos extra',
  includedIn: (roleName: string) => `Ya incluido en ${roleName}`,
  summaryLabel: 'Resumen de cambios',
  summary: 'Cambios:',
  added: (label: string) => `+ ${label}`,
  removed: (label: string) => `− ${label}`,
  emptyDraft: 'Para quitarle el acceso, usa «Quitar todo».',
  unmanaged: 'Gestionado por backend',
  unmanagedHint: 'Estos roles y permisos no se pueden cambiar desde aquí.',
} as const;

export interface AssignmentFormProps {
  mode: 'add' | 'edit';
  email: string;
  onEmailChange?: (value: string) => void;
  // Lets the dialog move the keyboard to the field when it refuses the email.
  emailRef?: Ref<HTMLInputElement>;
  emailError: string | null;
  // Shown under the email field only while there is an error, e.g. «Editarla» for a person who
  // already has an assignment.
  emailErrorAction?: ReactNode;
  // Only the roles this screen may grant; the caller filters the catalog.
  roles: readonly CatalogRole[];
  extras: readonly Permission[];
  draft: AssignmentDraft;
  onDraftChange: (draft: AssignmentDraft) => void;
  // The name of a marked role that already grants this permission, or null.
  includedBy: (permission: Permission) => string | null;
  unmanaged: { roles: PanelRole[]; extras: string[] };
  summary: ChangeSummary | null;
  summaryRef?: Ref<HTMLElement>;
}

interface SummaryItem {
  key: string;
  label: string;
}

// A blank display name is tolerated by the API; the id is what the person can still recognise.
const roleLabel = (role: PanelRole): string => (role.name.trim() === '' ? role.id : role.name);

function describeRole(role: CatalogRole): string | null {
  const labels = role.permissions.filter(isKnownPermission).map(permissionLabel);
  return labels.length === 0 ? null : labels.join(' · ');
}

const toggle = (ids: readonly string[], id: string, checked: boolean): string[] =>
  checked ? [...ids.filter((other) => other !== id), id] : ids.filter((other) => other !== id);

export interface ChangeSummaryLineProps {
  summary: ChangeSummary;
  // The roles the summary may name; an id outside them shows as itself.
  roles: readonly CatalogRole[];
  summaryRef?: Ref<HTMLElement>;
}

// «Cambios: + añadido · − quitado», a focusable group the form shows above the dialog's actions.
// Nothing at all while nothing has changed.
export function ChangeSummaryLine({
  summary,
  roles,
  summaryRef,
}: ChangeSummaryLineProps): ReactElement | null {
  if (!hasChanges(summary)) return null;
  const roleName = (id: string): string => roles.find((role) => role.id === id)?.name ?? id;
  const items: SummaryItem[] = [
    ...summary.addedRoles.map((id) => ({
      key: `+role:${id}`,
      label: COPY.added(roleName(id)),
    })),
    ...summary.removedRoles.map((id) => ({
      key: `-role:${id}`,
      label: COPY.removed(roleName(id)),
    })),
    ...summary.addedExtras.map((id) => ({
      key: `+extra:${id}`,
      label: COPY.added(permissionLabel(id)),
    })),
    ...summary.removedExtras.map((id) => ({
      key: `-extra:${id}`,
      label: COPY.removed(permissionLabel(id)),
    })),
  ];
  return (
    <Box
      role="group"
      aria-label={COPY.summaryLabel}
      tabIndex={-1}
      sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
      {...(summaryRef !== undefined ? { ref: summaryRef } : {})}
    >
      <Text component="span" variant="body2" bold>
        {COPY.summary}
      </Text>
      {items.map((item, index) => (
        <Fragment key={item.key}>
          {index > 0 && (
            <Text component="span" variant="body2" color="muted">
              ·
            </Text>
          )}
          <RoleChip label={item.label} />
        </Fragment>
      ))}
    </Box>
  );
}

// The body of the add/edit dialog. It holds no state of its own: the dialog owns the draft and its
// baseline, so a 409 reload can swap the baseline while every mark the person made stays put.
export function AssignmentForm({
  mode,
  email,
  onEmailChange,
  emailRef,
  emailError,
  emailErrorAction,
  roles,
  extras,
  draft,
  onDraftChange,
  includedBy,
  unmanaged,
  summary,
  summaryRef,
}: AssignmentFormProps): ReactElement {
  const hasUnmanaged = unmanaged.roles.length > 0 || unmanaged.extras.length > 0;

  return (
    <Stack spacing={3}>
      {mode === 'add' ? (
        <Stack spacing={1}>
          <TextField
            label={COPY.email}
            value={email}
            onChange={(value) => {
              onEmailChange?.(value);
            }}
            placeholder={COPY.emailPlaceholder}
            fullWidth
            autoFocus
            {...(emailRef !== undefined ? { ref: emailRef } : {})}
            {...(emailError !== null ? { error: true, helperText: emailError } : {})}
          />
          {emailError !== null && emailErrorAction}
        </Stack>
      ) : (
        <Stack spacing={0.5}>
          <Text variant="body2" color="muted">
            {COPY.email}
          </Text>
          <Text>{email}</Text>
        </Stack>
      )}

      <Stack spacing={1}>
        <Text component="h3" variant="subtitle2" bold>
          {COPY.roles}
        </Text>
        {roles.map((role, index) => {
          const description = describeRole(role);
          return (
            <Checkbox
              key={role.id}
              // Editing is a choice of roles, so the keyboard starts on the first one; adding
              // starts on the email instead.
              autoFocus={mode === 'edit' && index === 0}
              checked={draft.roles.includes(role.id)}
              onChange={(checked) => {
                onDraftChange({ ...draft, roles: toggle(draft.roles, role.id, checked) });
              }}
              label={role.name}
              {...(description !== null ? { description } : {})}
            />
          );
        })}
      </Stack>

      {extras.length > 0 && (
        <Stack spacing={1}>
          <Text component="h3" variant="subtitle2" bold>
            {COPY.extras}
          </Text>
          {extras.map((permission) => {
            const includedIn = includedBy(permission);
            return (
              <Checkbox
                key={permission}
                checked={draft.extraPermissions.includes(permission)}
                onChange={(checked) => {
                  onDraftChange({
                    ...draft,
                    extraPermissions: toggle(draft.extraPermissions, permission, checked),
                  });
                }}
                label={permissionLabel(permission)}
                {...(includedIn !== null ? { description: COPY.includedIn(includedIn) } : {})}
              />
            );
          })}
        </Stack>
      )}

      {hasUnmanaged && (
        <Stack spacing={1}>
          <Text component="h3" variant="subtitle2" bold>
            {COPY.unmanaged}
          </Text>
          <Text variant="body2" color="muted">
            {COPY.unmanagedHint}
          </Text>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {unmanaged.roles.map((role) => (
              <RoleChip key={`role:${role.id}`} label={roleLabel(role)} unknown />
            ))}
            {unmanaged.extras.map((id) => (
              <RoleChip key={`extra:${id}`} label={id} unknown />
            ))}
          </Box>
        </Stack>
      )}

      {summary !== null && (
        <ChangeSummaryLine
          summary={summary}
          roles={roles}
          {...(summaryRef !== undefined ? { summaryRef } : {})}
        />
      )}

      {mode === 'edit' && isEmptyDraft(draft) && (
        <Text variant="body2" color="muted">
          {COPY.emptyDraft}
        </Text>
      )}
    </Stack>
  );
}
